import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { clearCachesForLogout } from '@/lib/cacheReset'
import { deepCamelize } from '@/lib/caseTransform'
import type { User, AuthState } from '@/types'

interface AuthActions {
  setAuth: (user: User, token: string, refreshToken?: string) => void
  setToken: (token: string) => void
  setRefreshToken: (refreshToken: string) => void
  logout: () => void
  setLoading: (loading: boolean) => void
}

export const useAuthStore = create<AuthState & { refreshToken: string | null } & AuthActions>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,

      setAuth: (user, token, refreshToken) =>
        set({
          user,
          token,
          refreshToken: refreshToken ?? null,
          isAuthenticated: true,
          isLoading: false,
        }),

      setToken: (token) => set({ token }),

      setRefreshToken: (refreshToken) => set({ refreshToken }),

      logout: () => {
        // Wipe user-scoped caches (query cache, persisted IDB client, SW
        // api-cache) so the next login on a shared browser can't see this
        // user's data. Fire-and-forget — never blocks logout. Every logout
        // path funnels through here (navbars, settings, client refresh-fail,
        // authGuard), so this is the single chokepoint.
        clearCachesForLogout()
        set({
          user: null,
          token: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        })
      },

      setLoading: (loading) => set({ isLoading: loading }),
    }),
    {
      name: 'treasurelog-auth',
      // v1: the app moved to a camelCase field convention (frontend arch
      // refactor). Pre-v1 persisted state holds a snake_case `user`
      // (`created_at`, `collection_count`, `has_password`); camelize it so a
      // user mid-session at deploy time keeps a well-shaped user object without
      // being logged out (token / isAuthenticated are unaffected either way).
      version: 1,
      migrate: (persistedState, version) => {
        if (version < 1) {
          return deepCamelize<AuthState & { refreshToken: string | null }>(persistedState)
        }
        return persistedState as AuthState & { refreshToken: string | null }
      },
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.setLoading(false)
        }
      },
    },
  ),
)
